import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useStatusContext } from '../App';
import { createSite, getDistributorTariffs, matchDistributorTariffs, type DistributorTariff } from '../api/client';
import { GLOBIRD_DEFAULT_FREE_WINDOW, GLOBIRD_FREE_WINDOW_OPTIONS } from '../utils/globird';
import { defaultFlowPowerForm, flowPowerCredentials, type FlowPowerForm } from '../utils/flowpower';
import { FlowPowerPlanFields } from '../components/site/FlowPowerPlanFields';
import type { SiteCreate } from '../types';
import {
  Loader2,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  MapPin,
  Battery,
  Sun,
  DollarSign,
  Zap,
} from 'lucide-react';

const NEM_REGIONS = [
  { value: 'NSW1', label: 'New South Wales' },
  { value: 'VIC1', label: 'Victoria' },
  { value: 'QLD1', label: 'Queensland' },
  { value: 'SA1', label: 'South Australia' },
  { value: 'TAS1', label: 'Tasmania' },
];

const BATTERY_ADAPTERS = [
  { value: 'tesla_gateway', label: 'Tesla Powerwall (Gateway)' },
  { value: 'tesla_api', label: 'Tesla Powerwall (Cloud API)' },
  { value: 'enphase', label: 'Enphase IQ Battery' },
  { value: 'solaredge', label: 'SolarEdge Home Battery' },
  { value: 'sungrow', label: 'Sungrow Battery' },
  { value: 'byd', label: 'BYD Battery' },
  { value: 'demo', label: 'Demo/Simulation Mode' },
];

type Step = 'location' | 'battery' | 'solar' | 'pricing' | 'review';

interface FormData {
  name: string;
  region: string;
  latitude: string;
  longitude: string;
  address_line1: string;
  city: string;
  state: string;
  postcode: string;
  battery_adapter: string;
  battery_ip: string;
  battery_password: string;
  battery_capacity_kwh: string;
  battery_max_charge_kw: string;
  battery_max_discharge_kw: string;
  solar_capacity_kw: string;
  solar_panels: string;
  solar_inverter: string;
  price_provider: string;
  amber_api_key: string;
  amber_site_id: string;
  globird_tariff_id: string;
  globird_free_window: string; // "HH:MM-HH:MM" — ZeroCharge free-charging window
  flowpower: FlowPowerForm;
}

const PRICE_PROVIDER_LABELS: Record<string, string> = {
  aemo: 'AEMO Wholesale',
  amber: 'Amber Electric',
  globird_zerohero: 'GloBird ZeroHero',
  flowpower_happy_hour: 'Flow Power Happy Hour',
};

const initialFormData: FormData = {
  name: '',
  region: 'NSW1',
  latitude: '',
  longitude: '',
  address_line1: '',
  city: '',
  state: '',
  postcode: '',
  battery_adapter: 'demo',
  battery_ip: '',
  battery_password: '',
  battery_capacity_kwh: '13.5',
  battery_max_charge_kw: '5',
  battery_max_discharge_kw: '5',
  solar_capacity_kw: '6.6',
  solar_panels: '22',
  solar_inverter: '',
  price_provider: 'aemo',
  amber_api_key: '',
  amber_site_id: '',
  globird_tariff_id: '',
  globird_free_window: `${GLOBIRD_DEFAULT_FREE_WINDOW.start}-${GLOBIRD_DEFAULT_FREE_WINDOW.end}`,
  flowpower: defaultFlowPowerForm,
};

export function SiteOnboarding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refresh: refreshStatus } = useStatusContext();
  const [currentStep, setCurrentStep] = useState<Step>('location');
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [globirdTariffs, setGlobirdTariffs] = useState<DistributorTariff[]>([]);

  // Load selectable GloBird tariffs once the user picks that provider. Prefer
  // an exact postcode match (CDR plan geography); fall back to the state filter.
  // A postcode can map to >1 distributor at network boundaries, so we keep all
  // candidates and only auto-select when there's exactly one.
  const siteState = (formData.region || '').toUpperCase().replace(/[0-9]+$/, '');
  const postcode = (formData.postcode || '').trim();
  useEffect(() => {
    if (formData.price_provider !== 'globird_zerohero') return;
    let cancelled = false;
    const load = /^\d{4}$/.test(postcode)
      ? matchDistributorTariffs(postcode)
      : getDistributorTariffs({ retailer: 'globird', state: siteState || undefined });
    load
      .then((rows) => {
        if (cancelled) return;
        setGlobirdTariffs(rows);
        // Auto-select the single unambiguous match.
        if (rows.length === 1) {
          setFormData((prev) =>
            prev.globird_tariff_id ? prev : { ...prev, globird_tariff_id: rows[0].id },
          );
        }
      })
      .catch(() => {
        if (!cancelled) setGlobirdTariffs([]);
      });
    return () => {
      cancelled = true;
    };
  }, [formData.price_provider, siteState, postcode]);

  const steps: Step[] = ['location', 'battery', 'solar', 'pricing', 'review'];
  const currentIndex = steps.indexOf(currentStep);

  const updateField = (field: Exclude<keyof FormData, 'flowpower'>, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const nextStep = () => {
    const nextIndex = currentIndex + 1;
    if (nextIndex < steps.length) {
      setCurrentStep(steps[nextIndex]);
    }
  };

  const prevStep = () => {
    const prevIndex = currentIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(steps[prevIndex]);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const siteData: SiteCreate = {
        name: formData.name || `${user?.email}'s Site`,
        region: formData.region,
        latitude: formData.latitude ? parseFloat(formData.latitude) : undefined,
        longitude: formData.longitude ? parseFloat(formData.longitude) : undefined,
        address_line1: formData.address_line1 || undefined,
        city: formData.city || undefined,
        state: formData.state || undefined,
        postcode: formData.postcode || undefined,
        country: 'Australia',
        battery_adapter: formData.battery_adapter,
        battery_config: {
          capacity_wh: parseFloat(formData.battery_capacity_kwh) * 1000,
          max_charge_w: parseFloat(formData.battery_max_charge_kw) * 1000,
          max_discharge_w: parseFloat(formData.battery_max_discharge_kw) * 1000,
        },
        battery_credentials:
          formData.battery_adapter !== 'demo'
            ? {
                ip_address: formData.battery_ip,
                password: formData.battery_password,
              }
            : undefined,
        solar_config: {
          capacity_kw: parseFloat(formData.solar_capacity_kw) || 0,
          panels: parseInt(formData.solar_panels) || 0,
          inverter: formData.solar_inverter || undefined,
        },
        price_provider: formData.price_provider,
        price_provider_credentials:
          formData.price_provider === 'amber'
            ? {
                api_key: formData.amber_api_key,
                site_id: formData.amber_site_id,
              }
            : formData.price_provider === 'globird_zerohero'
            ? {
                tariff_id: formData.globird_tariff_id || undefined,
                state: siteState || undefined,
                free_window_start: formData.globird_free_window.split('-')[0],
                free_window_end: formData.globird_free_window.split('-')[1],
              }
            : formData.price_provider === 'flowpower_happy_hour'
            ? flowPowerCredentials(formData.flowpower, formData.region)
            : undefined,
        optimizer_engine: 'emhass',
      };

      const newSite = await createSite(siteData);
      // Refresh the sites status so navigation back to / works correctly
      refreshStatus();
      navigate(`/site/${newSite.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create site');
    } finally {
      setSubmitting(false);
    }
  };

  const stepIcon = (step: Step) => {
    switch (step) {
      case 'location':
        return <MapPin className="w-5 h-5" />;
      case 'battery':
        return <Battery className="w-5 h-5" />;
      case 'solar':
        return <Sun className="w-5 h-5" />;
      case 'pricing':
        return <DollarSign className="w-5 h-5" />;
      case 'review':
        return <CheckCircle2 className="w-5 h-5" />;
    }
  };

  const stepTitle = (step: Step) => {
    switch (step) {
      case 'location':
        return 'Location';
      case 'battery':
        return 'Battery';
      case 'solar':
        return 'Solar';
      case 'pricing':
        return 'Pricing';
      case 'review':
        return 'Review';
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-brand-orange/10 rounded-full flex items-center justify-center mx-auto">
          <Zap className="w-8 h-8 text-brand-orange" />
        </div>
        <h1 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">
          Set Up Your Site
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Configure your battery optimization system in a few easy steps.
        </p>
      </div>

      {/* Progress Steps */}
      <div className="flex justify-between mb-8">
        {steps.map((step, index) => (
          <div
            key={step}
            className={`flex flex-col items-center ${
              index <= currentIndex ? 'text-brand-orange' : 'text-gray-400'
            }`}
          >
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center ${
                index < currentIndex
                  ? 'bg-brand-orange text-white'
                  : index === currentIndex
                  ? 'border-2 border-brand-orange'
                  : 'border-2 border-gray-300 dark:border-gray-600'
              }`}
            >
              {index < currentIndex ? (
                <CheckCircle2 className="w-5 h-5" />
              ) : (
                stepIcon(step)
              )}
            </div>
            <span className="mt-2 text-xs font-medium">{stepTitle(step)}</span>
          </div>
        ))}
      </div>

      {/* Form Card */}
      <div className="card p-6">
        {/* Step Content */}
        {currentStep === 'location' && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Site Location
            </h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Site Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="My Home"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                NEM Region
              </label>
              <select
                value={formData.region}
                onChange={(e) => updateField('region', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
              >
                {NEM_REGIONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Latitude
                </label>
                <input
                  type="number"
                  step="any"
                  value={formData.latitude}
                  onChange={(e) => updateField('latitude', e.target.value)}
                  placeholder="-33.8688"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Longitude
                </label>
                <input
                  type="number"
                  step="any"
                  value={formData.longitude}
                  onChange={(e) => updateField('longitude', e.target.value)}
                  placeholder="151.2093"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Address
              </label>
              <input
                type="text"
                value={formData.address_line1}
                onChange={(e) => updateField('address_line1', e.target.value)}
                placeholder="123 Solar Street"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  City
                </label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => updateField('city', e.target.value)}
                  placeholder="Sydney"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  State
                </label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => updateField('state', e.target.value)}
                  placeholder="NSW"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Postcode
                </label>
                <input
                  type="text"
                  value={formData.postcode}
                  onChange={(e) => updateField('postcode', e.target.value)}
                  placeholder="2000"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                />
              </div>
            </div>
          </div>
        )}

        {currentStep === 'battery' && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Battery System
            </h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Battery Type
              </label>
              <select
                value={formData.battery_adapter}
                onChange={(e) => updateField('battery_adapter', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
              >
                {BATTERY_ADAPTERS.map((b) => (
                  <option key={b.value} value={b.value}>
                    {b.label}
                  </option>
                ))}
              </select>
            </div>

            {formData.battery_adapter !== 'demo' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Gateway IP Address
                  </label>
                  <input
                    type="text"
                    value={formData.battery_ip}
                    onChange={(e) => updateField('battery_ip', e.target.value)}
                    placeholder="192.168.1.100"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Password
                  </label>
                  <input
                    type="password"
                    value={formData.battery_password}
                    onChange={(e) => updateField('battery_password', e.target.value)}
                    placeholder="Gateway password"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                  />
                </div>
              </>
            )}

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Capacity (kWh)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.battery_capacity_kwh}
                  onChange={(e) => updateField('battery_capacity_kwh', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Max Charge (kW)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.battery_max_charge_kw}
                  onChange={(e) => updateField('battery_max_charge_kw', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Max Discharge (kW)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.battery_max_discharge_kw}
                  onChange={(e) => updateField('battery_max_discharge_kw', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                />
              </div>
            </div>
          </div>
        )}

        {currentStep === 'solar' && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Solar System
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  System Capacity (kW)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.solar_capacity_kw}
                  onChange={(e) => updateField('solar_capacity_kw', e.target.value)}
                  placeholder="6.6"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Number of Panels
                </label>
                <input
                  type="number"
                  value={formData.solar_panels}
                  onChange={(e) => updateField('solar_panels', e.target.value)}
                  placeholder="22"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Inverter Model (optional)
              </label>
              <input
                type="text"
                value={formData.solar_inverter}
                onChange={(e) => updateField('solar_inverter', e.target.value)}
                placeholder="Fronius Primo 6.0"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
              />
            </div>

          </div>
        )}

        {currentStep === 'pricing' && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Electricity Pricing
            </h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Price Provider
              </label>
              <select
                value={formData.price_provider}
                onChange={(e) => updateField('price_provider', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
              >
                <option value="aemo">AEMO Wholesale Prices</option>
                <option value="amber">Amber Electric</option>
                <option value="globird_zerohero">GloBird ZeroHero</option>
                <option value="flowpower_happy_hour">Flow Power Happy Hour</option>
              </select>
            </div>

            {formData.price_provider === 'flowpower_happy_hour' && (
              <FlowPowerPlanFields
                value={formData.flowpower}
                onChange={(flowpower) => setFormData((prev) => ({ ...prev, flowpower }))}
                region={formData.region}
              />
            )}

            {formData.price_provider === 'globird_zerohero' && (
              <>
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    GloBird ZeroHero is a fixed-tariff plan: a free daytime charging
                    window (currently 12pm&ndash;3pm), boosted feed-in 6&ndash;9pm. Pick
                    your distributor tariff below; we apply the ZeroHero windows
                    automatically.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Distributor Tariff
                  </label>
                  <select
                    value={formData.globird_tariff_id}
                    onChange={(e) => updateField('globird_tariff_id', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                  >
                    <option value="">Select your distributor tariff</option>
                    {globirdTariffs.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.distributor} &mdash; {t.name}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    {globirdTariffs.length === 0
                      ? `No tariffs listed for ${siteState || 'your state'} yet — you can add one later in site settings.`
                      : 'Your general usage rate comes from this tariff; the feed-in windows are set by the plan.'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Free Charging Window
                  </label>
                  <select
                    value={formData.globird_free_window}
                    onChange={(e) => updateField('globird_free_window', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                  >
                    {GLOBIRD_FREE_WINDOW_OPTIONS.map((o) => (
                      <option key={`${o.start}-${o.end}`} value={`${o.start}-${o.end}`}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Match this to the free (ZeroCharge) window on your GloBird plan
                    documents — you can change it later in site settings.
                  </p>
                </div>
              </>
            )}

            {formData.price_provider === 'amber' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Amber API Key
                  </label>
                  <input
                    type="password"
                    value={formData.amber_api_key}
                    onChange={(e) => updateField('amber_api_key', e.target.value)}
                    placeholder="psk_..."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Get this from the Amber app: Settings &rarr; For Developers
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Amber Site ID
                  </label>
                  <input
                    type="text"
                    value={formData.amber_site_id}
                    onChange={(e) => updateField('amber_site_id', e.target.value)}
                    placeholder="01..."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                  />
                </div>
              </>
            )}

            {formData.price_provider === 'aemo' && (
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  AEMO wholesale prices will be fetched automatically for your region.
                  For accurate pricing, consider using Amber Electric which includes
                  network and retail costs.
                </p>
              </div>
            )}
          </div>
        )}

        {currentStep === 'review' && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Review Your Configuration
            </h2>

            <div className="space-y-4">
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <h3 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Location
                </h3>
                <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  <p>Name: {formData.name || 'My Site'}</p>
                  <p>Region: {NEM_REGIONS.find((r) => r.value === formData.region)?.label}</p>
                  {formData.city && <p>Location: {formData.city}, {formData.state}</p>}
                </div>
              </div>

              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <h3 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                  <Battery className="w-4 h-4" /> Battery
                </h3>
                <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  <p>Type: {BATTERY_ADAPTERS.find((b) => b.value === formData.battery_adapter)?.label}</p>
                  <p>Capacity: {formData.battery_capacity_kwh} kWh</p>
                  <p>Max Power: {formData.battery_max_charge_kw} kW charge / {formData.battery_max_discharge_kw} kW discharge</p>
                </div>
              </div>

              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <h3 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                  <Sun className="w-4 h-4" /> Solar
                </h3>
                <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  <p>System Size: {formData.solar_capacity_kw} kW</p>
                  <p>Panels: {formData.solar_panels}</p>
                  <p>Forecast: Weather-based (automatic)</p>
                </div>
              </div>

              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <h3 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                  <DollarSign className="w-4 h-4" /> Pricing
                </h3>
                <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  <p>Provider: {PRICE_PROVIDER_LABELS[formData.price_provider] ?? formData.price_provider}</p>
                </div>
              </div>
            </div>

            {error && (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="mt-8 flex justify-between">
          <button
            onClick={prevStep}
            disabled={currentIndex === 0}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>

          {currentStep === 'review' ? (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex items-center gap-2 px-6 py-2 bg-brand-orange text-white rounded-full hover:bg-brand-orange-dark disabled:opacity-50"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4" />
              )}
              Create Site
            </button>
          ) : (
            <button
              onClick={nextStep}
              className="flex items-center gap-2 px-6 py-2 bg-brand-orange text-white rounded-full hover:bg-brand-orange-dark"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
